import React, { useState, useEffect, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import backendAPI from '../../utils/backendAPI'
import { ToastContext } from '../../App'
import { logout } from '../../actions/userActions'
import '../../css/HomePage.css'
import MerchantSidebarMenu from '../../components/MerchantSidebarMenu'


// MerchantApprovals builds a page for admin users to approve pending
// merchant users. Once an approval is executed, the merchant's status is updated
function MerchantApprovals() {
  const { userInfo } = useSelector((state) => state.userLogin)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const showToast = useContext(ToastContext)

  const { t } = useTranslation()

  const [merchants, setMerchants] = useState([])  // initialize merchants' state

  const logoutHandler = () => {
    showToast?.('You have been logged out successfully.', 'info')
    dispatch(logout())
    navigate('/')
  }

  // get all pending merchants (Cloud Functions + Firestore; requires admin claim)
  useEffect(() => {
    if (!userInfo) return

    backendAPI
      .get('/api/admin/pending-merchants/')
      .then((res) => setMerchants(Array.isArray(res.data) ? res.data : []))
      .catch(() => showToast?.('Failed to load all pending merchants.', 'error'))
  }, [userInfo])

  const handleApprove = async (id) => {
    try {
      await backendAPI.post(`/api/admin/approve-merchant/${id}/`)
      setMerchants((prev) => prev.filter((m) => m.id !== id))
    } catch {
      showToast?.('Failed to approve merchant.', 'error')
    }
  }

  const handleReject = async (id) => {
    try {
      await backendAPI.post(`/api/admin/reject-merchant/${id}/`)
      setMerchants((prev) => prev.filter((m) => m.id !== id))
    } catch {
      showToast?.('Failed to reject merchant.', 'error')
    }
  }

  return (
    <div className="klerk-app-container">
      <div className="klerk-sidebar">
        <div className="klerk-logo">Klerk</div>
        <MerchantSidebarMenu dashboardEnd />
      </div>

      <div className="klerk-main-content">
        <div className="klerk-header d-flex justify-content-between align-items-center mb-4">
          <h4 className="page-title m-0">{t('merchantApprovals.header')}</h4>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <div className="user-profile-widget d-flex align-items-center">
              <span className="store-name">
                {userInfo.username || userInfo.email || "Admin"}{" "}
              </span>
              <span className="status-badge">{t('profile.active')}</span>
              <button
                  type="button"
                  className="user-avatar"
                  onClick={() => navigate("/merchant-profile/")}
                  title="View profile"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
              >
                  <span role="img" aria-label="user">
                  🧑‍💼
                  </span>
              </button>
            </div>
            <button
              type="button"
              className="klerk-btn btn-secondary klerk-header-logout"
              onClick={logoutHandler}
            >
              {t('profile.logOut')}
            </button>
          </div>
        </div>

        <div className="klerk-card">
          {merchants.length === 0 ? (
            <p className="text-muted small mb-0 py-2">No pending merchants.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Business Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Signup Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.map((m) => (
                    <tr key={m.id}>
                      <td>{m.business_name || 'N/A'}</td>
                      <td>{m.email}</td>
                      <td>{m.phone || 'N/A'}</td>
                      <td>{m.address || 'N/A'}</td>
                      <td>{new Date(m.date_joined).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className="klerk-btn btn-primary"
                            style={{ padding: '4px 14px', fontSize: '13px' }}
                            onClick={() => handleApprove(m.id)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="klerk-btn btn-secondary"
                            style={{ padding: '4px 14px', fontSize: '13px' }}
                            onClick={() => handleReject(m.id)}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MerchantApprovals
